import { Pinwheel } from "ldrs/react";
import "ldrs/react/Pinwheel.css";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  BiDownArrow,
  BiUpArrow
} from "react-icons/bi";
import { HiOutlineTable } from "react-icons/hi";
import { useSelector } from "react-redux";
import Api from "../../service/http";
import { formatLocalTradeDateTime } from "../../utils/marketTime";
import { MarketDataDelaySubscribeInline } from "./components/MarketDataDelaySubscribeHeader";
import RecencyBadge from "./components/RecencyBadge";
import SelectTicker from "./components/SelectTicker";
import SubscribeUnblurOverlay from "./components/SubscribeUnblurOverlay";
import { TIME_SALES_COLUMN_KEYS } from "./constants";
import useMarketDataSubscription from "./hooks/useMarketDataSubscription";

const getIngestionPercent = (ingestion) => {
  if (typeof ingestion?.progress !== "number") return null;
  return Math.round(ingestion.progress * 100);
};

/** Polygon fair-queue: no rows yet while status is running and no chunk has finished. */
const isIngestionAwaitingFirstChunk = (ingestion) =>
  ingestion?.status === "running" && Number(ingestion?.done_chunks ?? 0) <= 0;

const canShowBiggestTable = (ingestion, biggestSnapshotReady) => {
  if (!biggestSnapshotReady) return false;
  if (ingestion?.status === "running") {
    return Number(ingestion?.done_chunks ?? 0) > 0;
  }
  return true;
};

const LOADER_STATUS_MESSAGES = [
  "Loading data…",
  "Please wait…",
  "Getting things ready…",
  "Loading your data…",
  "Fetching information…",
  "Preparing your data…",
  "Almost there…",
  "Just a moment…",
  "Processing your request…",
  "Getting everything ready…",
  "Loading, please wait…",
  "Hang tight…",
  "Working on it…",
  "Please wait while we load your data…",
  "Setting things up…",
];

const LOADER_MESSAGE_INTERVAL_MS = 2800;

/** Matches mobile `Colors.primaryColor` / `Colors.lightPurple` */
const BRAND_PRIMARY = "#5244D3";
const BRAND_PRIMARY_DARK = "#AAA2F5";

const IngestionProgress = ({ ingestion, theme }) => {
  const percent = getIngestionPercent(ingestion);
  const isDark = theme === "dark";

  return (
    <div
      role="status"
      className={`border-b px-4 py-2 ${isDark ? "border-slate-800 bg-slate-900/60" : "border-slate-100 bg-white"}`}
    >
      <div className="mb-1 flex items-center justify-between text-xs font-medium text-slate-500">
        <span>Processing data…</span>
        {percent !== null && <span className="tabular-nums">{percent}%</span>}
      </div>
      <div className={`h-1.5 w-full overflow-hidden rounded-full ${isDark ? "bg-slate-800" : "bg-slate-200"}`}>
        <div
          className={`h-full rounded-full bg-[#5244D3] transition-all duration-500 ${percent === null ? "w-1/4 animate-pulse" : ""}`}
          style={percent === null ? undefined : { width: `${Math.max(3, percent)}%` }}
        />
      </div>
    </div>
  );
};

const OrderFlowEmptyState = ({ theme, ticker, processing }) => {
  const isDark = theme === "dark";

  return (
    <div className="flex h-full min-h-[16rem] flex-1 items-center justify-center p-6">
      <div
        className={`w-full max-w-md rounded-2xl border px-8 py-10 text-center shadow-xl ${
          isDark
            ? "border-slate-700/70 bg-slate-900/70 shadow-black/30"
            : "border-[#5244D3]/15 bg-white shadow-[#5244D3]/10"
        }`}
        role="status"
      >
        <div
          className={`mx-auto mb-5 flex h-16 w-16 items-center justify-center rounded-full border ${
            isDark
              ? "border-violet-400/25 bg-violet-500/15"
              : "border-[#5244D3]/15 bg-[#5244D3]/10"
          }`}
        >
          <HiOutlineTable
            size={30}
            className={isDark ? "text-[#AAA2F5]" : "text-[#5244D3]"}
          />
        </div>
        <p
          className={`text-base font-semibold tracking-tight ${
            isDark ? "text-slate-100" : "text-slate-900"
          }`}
        >
          {processing ? "Processing data…" : "No order flow for this symbol"}
        </p>
        <p className={`mt-2 text-sm leading-relaxed ${isDark ? "text-slate-400" : "text-slate-500"}`}>
          {processing
            ? `We're loading options time & sales for ${ticker}. This can take a moment on the first request.`
            : `There are no options prints for ${ticker} in today's session yet. Try another symbol or check back after the market opens.`}
        </p>
      </div>
    </div>
  );
};

const InitialDataLoader = ({ theme, ingestionPercent }) => {
  const isDark = theme === "dark";
  const showPercent = ingestionPercent !== null && ingestionPercent > 0;
  const [messageIndex, setMessageIndex] = useState(0);

  useEffect(() => {
    const id = setInterval(() => {
      setMessageIndex((prev) => (prev + 1) % LOADER_STATUS_MESSAGES.length);
    }, LOADER_MESSAGE_INTERVAL_MS);
    return () => clearInterval(id);
  }, []);

  const statusMessage = LOADER_STATUS_MESSAGES[messageIndex];

  return (
    <div className="flex h-full min-h-[16rem] flex-1 items-center justify-center p-6">
      <div
        className={`w-full max-w-md rounded-2xl border px-8 py-10 text-center shadow-xl ${
          isDark
            ? "border-slate-700/70 bg-slate-900/70 shadow-black/30"
            : "border-slate-200/90 bg-white shadow-slate-300/40"
        }`}
        role="status"
        aria-live="polite"
        aria-busy="true"
        aria-label={showPercent ? `${statusMessage} ${ingestionPercent}%` : statusMessage}
      >
        <div className="mx-auto mb-6 flex items-center justify-center">
          <Pinwheel
            size="60"
            speed="1"
            stroke="3.5"
            color={isDark ? BRAND_PRIMARY_DARK : BRAND_PRIMARY}
          />
        </div>
        <p
          key={messageIndex}
          className={`text-base font-semibold tracking-tight transition-opacity duration-300 ${
            isDark ? "text-slate-100" : "text-slate-900"
          }`}
        >
          {statusMessage}
        </p>
        {showPercent ? (
          <p className={`mt-2 text-sm tabular-nums ${isDark ? "text-slate-400" : "text-slate-500"}`}>
            {ingestionPercent}% complete
          </p>
        ) : null}
        <div
          className={`mt-7 h-1.5 w-full overflow-hidden rounded-full ${
            isDark ? "bg-slate-800" : "bg-slate-100"
          }`}
        >
          {showPercent ? (
            <div
              className="h-full rounded-full bg-[#5244D3] transition-all duration-500 ease-out"
              style={{ width: `${Math.max(4, ingestionPercent)}%` }}
            />
          ) : (
            <div className="h-full w-2/5 animate-pulse rounded-full bg-[#5244D3]/80" />
          )}
        </div>
      </div>
    </div>
  );
};

const OptionsTimeSales = ({ selected, setSelected, isActive }) => {
  const subscribed = useMarketDataSubscription();
  const { theme } = useSelector((state) => state.theme);
  const [data, setData] = useState([]);
  const dataRef = useRef([]);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const [biggestData, setBiggestData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [isFetchingBiggest, setIsFetchingBiggest] = useState(false);
  const [isFetchingTimeSale, setIsFetchingTimeSale] = useState(false);
  const [recency, setRecency] = useState(null);
  const [delayMinutes, setDelayMinutes] = useState(15);
  /** Backend ingestion run for this ticker: { status, progress, ... } or null. */
  const [ingestion, setIngestion] = useState(null);
  const [hasSettledLoad, setHasSettledLoad] = useState(false);
  /** First page of time-sale is ready (not null / still ingesting). */
  const [timeSaleInitialReady, setTimeSaleInitialReady] = useState(false);
  /** At least one biggest-data response received for the current ticker (polls stay silent). */
  const [biggestSnapshotReady, setBiggestSnapshotReady] = useState(false);

  // Refs for tracking values without triggering re-renders
  const totalDataRef = useRef(null);
  const isFetchingBiggestRef = useRef(false);
  const isFetchingTimeSaleRef = useRef(false);
  const pollingRef = useRef(null);
  const retryRef = useRef(null);
  const loadedTickerRef = useRef(null);
  const pageRef = useRef(1);
  /** Bumped on ticker change so in-flight responses for the previous symbol are ignored. */
  const loadGenerationRef = useRef(0);
  /** Serializes market-data HTTP so a new call waits for the previous response. */
  const marketDataQueueRef = useRef(Promise.resolve());
  const marketDataAbortRef = useRef(null);
  const marketDataQueueEpochRef = useRef(0);
  const prevSelectedForLoadRef = useRef(null);
  const pendingLoadTickerRef = useRef(null);
  const lastKickedLoadTickerRef = useRef(null);
  const prevIsActiveRef = useRef(isActive);
  const skipSortEffectRef = useRef(false);

  const isAbortError = (error) =>
    error?.code === "ERR_CANCELED" ||
    error?.name === "CanceledError" ||
    error?.name === "AbortError";

  const cancelMarketDataRequests = () => {
    marketDataQueueEpochRef.current += 1;
    if (pollingRef.current) {
      clearTimeout(pollingRef.current);
      pollingRef.current = null;
    }
    if (retryRef.current) {
      clearTimeout(retryRef.current);
      retryRef.current = null;
    }
    if (marketDataAbortRef.current) {
      marketDataAbortRef.current.abort();
    }
    marketDataAbortRef.current = new AbortController();
    marketDataQueueRef.current = Promise.resolve();
  };

  const marketDataSignal = () => {
    if (!marketDataAbortRef.current) {
      marketDataAbortRef.current = new AbortController();
    }
    return marketDataAbortRef.current.signal;
  };

  const enqueueMarketDataTask = (task) => {
    const epoch = marketDataQueueEpochRef.current;
    const run = marketDataQueueRef.current.then(async () => {
      if (epoch !== marketDataQueueEpochRef.current) return;
      await task();
    });
    marketDataQueueRef.current = run.catch(() => {});
    return run;
  };

  const isStaleLoad = (tickerAtStart, generationAtStart) =>
    loadGenerationRef.current !== generationAtStart ||
    loadedTickerRef.current !== tickerAtStart;

  useEffect(() => {
    dataRef.current = data;
  }, [data]);

  useEffect(() => {
    pageRef.current = page;
  }, [page]);

  const DEFAULT_SORT_COL = "qty";
  const DEFAULT_SORT_ORDER = "desc";
  /** null = default server sort (no sort UI) */
  const [sortCol, setSortCol] = useState(null);
  const [sortOrder, setSortOrder] = useState(DEFAULT_SORT_ORDER);

  const [columnWidths, setColumnWidths] = useState({
    time: 170,
    option: 150,
    qty: 80,
    price: 80,
    exchange: 120,
    market: 80,
    delta: 80,
    iv: 80,
    underlying: 100,
    condition: 180,
  });

  const executeBiggestFetch = useCallback(
    async (tickerAtStart, generationAtStart, { silent = false } = {}) => {
      if (!tickerAtStart || tickerAtStart === "all") return false;
      if (isStaleLoad(tickerAtStart, generationAtStart)) return false;

      if (!silent) {
        isFetchingBiggestRef.current = true;
        setIsFetchingBiggest(true);
      }
      let shouldScheduleNext = false;

      const signal = marketDataSignal();
      if (!signal || signal.aborted) return false;

      try {
        const res = await Api.post(
          "/app/1_8_0/marketdata/biggest-data",
          { ticker: tickerAtStart },
          { signal }
        );

        if (isStaleLoad(tickerAtStart, generationAtStart)) {
          return false;
        }

        if (res.data?.status === "success") {
          if (res.data.recency) {
            setRecency(res.data.recency);
          }
          if (typeof res.data.delay_minutes === "number") {
            setDelayMinutes(res.data.delay_minutes);
          }
          if (res.data.ingestion !== undefined) {
            setIngestion(res.data.ingestion);
          }
          const stillIngesting = res.data.ingestion?.status === "running";
          const newTotalData = res.data.total_data;
          const newBiggestData = res.data.biggest_data || [];
          const hasChanged = newTotalData !== totalDataRef.current;

          setBiggestData(newBiggestData);
          totalDataRef.current = newTotalData;

          if (newTotalData === null || hasChanged || stillIngesting) {
            shouldScheduleNext = true;
          }

          if (generationAtStart === loadGenerationRef.current) {
            setBiggestSnapshotReady(true);
          }
        }
      } catch (error) {
        if (isAbortError(error)) return false;
        console.error("Failed to fetch biggest data", error);
        shouldScheduleNext = true;
      } finally {
        if (!silent && generationAtStart === loadGenerationRef.current) {
          isFetchingBiggestRef.current = false;
          setIsFetchingBiggest(false);
        }
      }

      return shouldScheduleNext;
    },
    []
  );

  const executeTimeSaleFetch = useCallback(
    async (tickerAtStart, generationAtStart, pageNum = 1) => {
      if (!tickerAtStart || tickerAtStart === "all") return;

      if (isStaleLoad(tickerAtStart, generationAtStart)) {
        return;
      }

      isFetchingTimeSaleRef.current = true;
      setIsFetchingTimeSale(true);
      if (pageNum === 1) setLoading(true);

      const signal = marketDataSignal();
      if (!signal || signal.aborted) return;

      try {
        const res = await Api.post(
          "/app/1_8_0/marketdata/time-sale",
          {
            ticker: tickerAtStart,
            page: pageNum,
            per_page: 50,
            sort_col: sortCol ?? DEFAULT_SORT_COL,
            sort_order: sortCol ? sortOrder : DEFAULT_SORT_ORDER,
          },
          { signal }
        );

        if (isStaleLoad(tickerAtStart, generationAtStart)) {
          return;
        }

        if (res.data?.status === "success") {
          if (res.data.recency) {
            setRecency(res.data.recency);
          }
          if (typeof res.data.delay_minutes === "number") {
            setDelayMinutes(res.data.delay_minutes);
          }
          const stillIngesting = res.data.ingestion?.status === "running";
          const paginatedData = res.data.data;
          const awaitingBackend = paginatedData == null;
          const newItems = paginatedData?.data || [];

          if (pageNum === 1) {
            setData(newItems);
            setTimeSaleInitialReady(!stillIngesting && !awaitingBackend);

            if (stillIngesting || awaitingBackend) {
              if (retryRef.current) clearTimeout(retryRef.current);
              const retryTicker = tickerAtStart;
              const retryGen = generationAtStart;
              retryRef.current = setTimeout(() => {
                retryRef.current = null;
                if (pageRef.current !== 1) return;
                if (
                  retryGen !== loadGenerationRef.current ||
                  loadedTickerRef.current !== retryTicker
                ) {
                  return;
                }
                enqueueMarketDataTask(() =>
                  executeTimeSaleFetch(retryTicker, retryGen, 1)
                );
              }, 5000);
            } else if (retryRef.current) {
              clearTimeout(retryRef.current);
              retryRef.current = null;
            }
          } else {
            setData((prev) => [...prev, ...newItems]);
          }

          setHasMore(!!paginatedData?.next_page_url);
          setPage(pageNum);
        }
      } catch (error) {
        if (isAbortError(error)) return;
        console.error("Failed to fetch time sale data", error);
      } finally {
        if (generationAtStart === loadGenerationRef.current) {
          isFetchingTimeSaleRef.current = false;
          setIsFetchingTimeSale(false);
          if (pageNum === 1) setLoading(false);
        }
      }
    },
    [sortCol, sortOrder]
  );

  const scheduleBiggestPoll = useCallback(
    (tickerAtStart, generationAtStart) => {
      if (pollingRef.current) clearTimeout(pollingRef.current);
      const pollEpoch = marketDataQueueEpochRef.current;
      pollingRef.current = setTimeout(() => {
        pollingRef.current = null;
        if (
          pollEpoch !== marketDataQueueEpochRef.current ||
          generationAtStart !== loadGenerationRef.current ||
          loadedTickerRef.current !== tickerAtStart
        ) {
          return;
        }
        enqueueMarketDataTask(async () => {
          if (
            pollEpoch !== marketDataQueueEpochRef.current ||
            generationAtStart !== loadGenerationRef.current ||
            loadedTickerRef.current !== tickerAtStart
          ) {
            return;
          }
          const pollAgain = await executeBiggestFetch(
            tickerAtStart,
            generationAtStart,
            { silent: true }
          );
          if (
            pollAgain &&
            generationAtStart === loadGenerationRef.current &&
            loadedTickerRef.current === tickerAtStart
          ) {
            scheduleBiggestPoll(tickerAtStart, generationAtStart);
          }
        });
      }, 5000);
    },
    [executeBiggestFetch]
  );

  const runTickerLoad = useCallback(
    (ticker, generationAtStart) => {
      const epoch = marketDataQueueEpochRef.current;
      return enqueueMarketDataTask(async () => {
        try {
          if (
            epoch !== marketDataQueueEpochRef.current ||
            generationAtStart !== loadGenerationRef.current ||
            loadedTickerRef.current !== ticker
          ) {
            return;
          }

          const timeSalePromise = executeTimeSaleFetch(ticker, generationAtStart, 1);
          const pollBiggest = await executeBiggestFetch(ticker, generationAtStart);
          skipSortEffectRef.current = false;
          if (
            epoch !== marketDataQueueEpochRef.current ||
            generationAtStart !== loadGenerationRef.current ||
            loadedTickerRef.current !== ticker
          ) {
            return;
          }
          await timeSalePromise;

          if (
            generationAtStart === loadGenerationRef.current &&
            loadedTickerRef.current === ticker
          ) {
            prevSelectedForLoadRef.current = ticker;
            pendingLoadTickerRef.current = null;
            lastKickedLoadTickerRef.current = null;
          }

          if (
            pollBiggest &&
            epoch === marketDataQueueEpochRef.current &&
            generationAtStart === loadGenerationRef.current &&
            loadedTickerRef.current === ticker
          ) {
            scheduleBiggestPoll(ticker, generationAtStart);
          }
        } finally {
          if (
            pendingLoadTickerRef.current === ticker &&
            generationAtStart !== loadGenerationRef.current
          ) {
            pendingLoadTickerRef.current = null;
            lastKickedLoadTickerRef.current = null;
          }
        }
      });
    },
    [executeBiggestFetch, executeTimeSaleFetch, scheduleBiggestPoll]
  );

  const fetchBiggestData = useCallback(
    (tickerParam) => {
      const ticker = tickerParam ?? selected;
      if (!ticker || ticker === "all") return Promise.resolve();
      const gen = loadGenerationRef.current;
      return enqueueMarketDataTask(async () => {
        const pollAgain = await executeBiggestFetch(ticker, gen, { silent: true });
        if (pollAgain) scheduleBiggestPoll(ticker, gen);
      });
    },
    [selected, executeBiggestFetch, scheduleBiggestPoll]
  );

  const fetchTimeSale = useCallback(
    (pageNum = 1, isManual = false, tickerParam) => {
      const ticker = tickerParam ?? selected;
      if (!ticker || ticker === "all") return Promise.resolve();
      if (pageNum === 1 && dataRef.current.length > 0 && !isManual) {
        return Promise.resolve();
      }
      const gen = loadGenerationRef.current;
      return enqueueMarketDataTask(() =>
        executeTimeSaleFetch(ticker, gen, pageNum)
      );
    },
    [selected, executeTimeSaleFetch]
  );

  const runTickerLoadRef = useRef(runTickerLoad);
  runTickerLoadRef.current = runTickerLoad;

  // Load when the user changes symbol; skip reload when only returning to this tab.
  useEffect(() => {
    const tabBecameActive = isActive && !prevIsActiveRef.current;
    prevIsActiveRef.current = isActive;

    if (!isActive || !selected || selected === "all") {
      if (!selected || selected === "all") {
        prevSelectedForLoadRef.current = null;
      }
      return () => {
        if (pollingRef.current) clearTimeout(pollingRef.current);
        if (retryRef.current) clearTimeout(retryRef.current);
      };
    }

    const ticker = String(selected);
    const selectedChanged = prevSelectedForLoadRef.current !== ticker;

    if (!selectedChanged && tabBecameActive) {
      return () => {
        if (pollingRef.current) clearTimeout(pollingRef.current);
        if (retryRef.current) clearTimeout(retryRef.current);
      };
    }

    if (!selectedChanged) {
      return () => {
        if (pollingRef.current) clearTimeout(pollingRef.current);
        if (retryRef.current) clearTimeout(retryRef.current);
      };
    }

    if (
      lastKickedLoadTickerRef.current === ticker &&
      pendingLoadTickerRef.current === ticker
    ) {
      return () => {
        if (pollingRef.current) clearTimeout(pollingRef.current);
        if (retryRef.current) clearTimeout(retryRef.current);
      };
    }

    loadGenerationRef.current += 1;
    const generationAtStart = loadGenerationRef.current;
    cancelMarketDataRequests();
    pendingLoadTickerRef.current = ticker;
    lastKickedLoadTickerRef.current = ticker;
    isFetchingBiggestRef.current = false;
    isFetchingTimeSaleRef.current = false;
    setIsFetchingBiggest(false);
    setIsFetchingTimeSale(false);
    setLoading(false);

    skipSortEffectRef.current = true;
    setData([]);
    setBiggestData([]);
    setIngestion(null);
    setSortCol(null);
    setSortOrder(DEFAULT_SORT_ORDER);
    totalDataRef.current = null;
    setPage(1);
    setHasMore(true);
    setHasSettledLoad(false);
    setTimeSaleInitialReady(false);
    setBiggestSnapshotReady(false);
    loadedTickerRef.current = ticker;

    runTickerLoadRef.current(ticker, generationAtStart);

    return () => {
      if (pollingRef.current) clearTimeout(pollingRef.current);
      if (retryRef.current) clearTimeout(retryRef.current);
    };
  }, [selected, isActive]);

  useEffect(() => {
    return () => {
      if (marketDataAbortRef.current) {
        marketDataAbortRef.current.abort();
      }
    };
  }, []);

  useEffect(() => {
    const hasTicker = Boolean(selected && selected !== "all");
    const ticker = hasTicker ? String(selected) : null;
    if (!ticker || loadedTickerRef.current !== ticker) {
      setHasSettledLoad(false);
      return;
    }
    if (isIngestionAwaitingFirstChunk(ingestion)) {
      setHasSettledLoad(false);
      return;
    }
    if (!biggestSnapshotReady || !timeSaleInitialReady) {
      setHasSettledLoad(false);
      return;
    }
    if (loading || isFetchingTimeSale) {
      setHasSettledLoad(false);
      return;
    }
    setHasSettledLoad(true);
  }, [
    selected,
    loading,
    isFetchingBiggest,
    isFetchingTimeSale,
    ingestion,
    biggestSnapshotReady,
    timeSaleInitialReady,
  ]);

  // Handle sorting/filtering change (not when ticker load resets sort)
  useEffect(() => {
    if (skipSortEffectRef.current) return;
    if (!selected || selected === "all" || !isActive) return;

    if (retryRef.current) clearTimeout(retryRef.current);
    setData([]);
    setPage(1);
    setHasMore(true);
    fetchTimeSale(1, true);
  }, [sortCol, sortOrder, isActive, fetchTimeSale]);

  // Infinite scroll listener
  const handleScroll = (e) => {
    if (!subscribed) return;
    const { scrollTop, scrollHeight, clientHeight } = e.target;
    if (scrollHeight - scrollTop <= clientHeight + 100 && hasMore && !isFetchingTimeSaleRef.current) {
      fetchTimeSale(page + 1, true); // Force next page call
    }
  };

  const sortColumn = (col) => {
    if (sortCol !== col) {
      setSortCol(col);
      setSortOrder("asc");
      return;
    }
    if (sortOrder === "asc") {
      setSortOrder("desc");
      return;
    }
    setSortCol(null);
    setSortOrder(DEFAULT_SORT_ORDER);
  };

  const getColor = (type) => {
    if (type === 1) return "text-green-500";
    if (type === 3) return "text-red-500";
    return theme === 'dark' ? "text-white" : "text-slate-900";
  };

  const getContractColor = (type) =>
    type === 1 ? "text-sky-400" : "text-purple-400";

  const biggestPut = useMemo(
    () => biggestData.find((d) => Number(d?.contract_type) === 2) ?? null,
    [biggestData]
  );
  const biggestCall = useMemo(
    () => biggestData.find((d) => Number(d?.contract_type) === 1) ?? null,
    [biggestData]
  );

  const hasTicker = Boolean(selected && selected !== "all");
  const hasAnyOrderFlow =
    data.length > 0 || Boolean(biggestCall) || Boolean(biggestPut);
  const isIngesting = ingestion?.status === "running";
  const ingestionAwaitingFirstChunk = isIngestionAwaitingFirstChunk(ingestion);
  const showBiggestTable = canShowBiggestTable(ingestion, biggestSnapshotReady);

  const showFullPageLoader = useMemo(() => {
    if (!hasTicker) return false;
    if (!biggestSnapshotReady) return true;
    if (ingestionAwaitingFirstChunk) return true;
    return false;
  }, [hasTicker, biggestSnapshotReady, ingestionAwaitingFirstChunk]);

  const showIngestionProgressBar = showBiggestTable && isIngesting;

  const showOrderFlowEmpty =
    hasTicker &&
    hasSettledLoad &&
    biggestSnapshotReady &&
    timeSaleInitialReady &&
    !showFullPageLoader &&
    !isIngesting &&
    !hasAnyOrderFlow;

  const showTimeSaleSectionLoader =
    hasTicker &&
    showBiggestTable &&
    !timeSaleInitialReady &&
    (loading || isFetchingTimeSale);

  const tableFrameBorderClass =
    theme === "dark"
      ? "w-max min-w-full rounded-xl border border-slate-700/70 bg-slate-900/40"
      : "w-max min-w-full rounded-xl border border-slate-200 bg-white";

  /** overflow-hidden breaks sticky headers inside the time & sales scroll region */
  const tableFrameClass = `${tableFrameBorderClass} overflow-hidden`;

  const scrollSurfaceClass =
    theme === "dark" ? "bg-slate-950/40" : "bg-slate-50";

  const headerRowClass = (sticky = false) => {
    const base =
      theme === "dark"
        ? "flex min-w-full bg-slate-800/95 text-slate-400 backdrop-blur-sm"
        : "flex min-w-full bg-slate-100/95 text-slate-600 backdrop-blur-sm";
    return sticky ? `${base} sticky top-0 z-30` : base;
  };

  const stripeRowClass = (rowIndex) => {
    const even = rowIndex % 2 === 0;
    if (theme === "dark") {
      return even
        ? "bg-slate-900/40 hover:bg-slate-800/55"
        : "bg-slate-800/25 hover:bg-slate-800/50";
    }
    return even ? "bg-white hover:bg-slate-50" : "bg-slate-50/95 hover:bg-slate-100/90";
  };

  const numericCellKeys = new Set(["qty", "price", "delta", "iv", "underlying", "market"]);

  const handleResize = (col) => (e) => {
    e.preventDefault();
    const startX = e.clientX;
    const startWidth = columnWidths[col];

    const onMouseMove = (moveEvent) => {
      const newWidth = Math.max(60, startWidth + moveEvent.clientX - startX);
      setColumnWidths((prev) => ({ ...prev, [col]: newWidth }));
    };

    const onMouseUp = () => {
      window.removeEventListener("mousemove", onMouseMove);
      window.removeEventListener("mouseup", onMouseUp);
    };

    window.addEventListener("mousemove", onMouseMove);
    window.addEventListener("mouseup", onMouseUp);
  };

  const renderHeader = (
    isBiggest = false,
    columnKeys = TIME_SALES_COLUMN_KEYS,
    sticky = false
  ) => {
    const effectiveSortCol = sortCol ?? DEFAULT_SORT_COL;
    const effectiveSortOrder = sortCol ? sortOrder : DEFAULT_SORT_ORDER;

    const renderCol = (label, key, sortable = true, isLast = false) => {
      const isActiveSort = effectiveSortCol === key;
      return (
      <div
        key={key}
        className={`group relative flex items-center justify-center px-3 py-2.5 text-center text-[11px] font-semibold uppercase tracking-wide ${
          isLast ? "flex-1" : ""
        } ${sortable ? "cursor-pointer select-none transition-colors hover:bg-black/5 dark:hover:bg-white/5" : ""}`}
        style={{ width: `${columnWidths[key]}px`, minWidth: 60 }}
        onClick={() => sortable && sortColumn(key)}
      >
        <div className="flex min-w-0 max-w-full items-center justify-center gap-1">
          <span className="truncate">{label}</span>
          {!isBiggest && sortable && isActiveSort && effectiveSortOrder === "asc" ? (
            <BiUpArrow size={8} className="shrink-0 text-blue-400 opacity-90" />
          ) : null}
          {!isBiggest && sortable && isActiveSort && effectiveSortOrder === "desc" ? (
            <BiDownArrow size={8} className="shrink-0 text-blue-400 opacity-90" />
          ) : null}
          {!isBiggest && sortable && !isActiveSort ? (
            <div className="flex shrink-0 flex-col -space-y-1 opacity-0 transition-opacity duration-150 group-hover:opacity-60">
              <BiUpArrow size={8} className="text-slate-500" />
              <BiDownArrow size={8} className="text-slate-500" />
            </div>
          ) : null}
        </div>
        <div
          onMouseDown={(e) => {
            e.stopPropagation();
            e.preventDefault();
            handleResize(key)(e);
          }}
          className="absolute top-0 right-0 w-1 h-full cursor-col-resize bg-blue-500/0 hover:bg-blue-500 transition-colors"
        />
      </div>
    );
    };

    const labels = {
      time: isBiggest ? "Today's Biggest" : "Time",
      option: "Options",
      qty: "Qty",
      price: "Price",
      exchange: "Exchange",
      market: "Market",
      delta: "Delta",
      iv: "IV",
      underlying: "Underlying",
      condition: "Conditions",
    };

    return (
      <div className={headerRowClass(sticky && !isBiggest)}>
        {columnKeys.map((key, idx) =>
          renderCol(
            labels[key] || key,
            key,
            !isBiggest && ["time", "option", "qty", "price"].includes(key),
            idx === columnKeys.length - 1
          )
        )}
      </div>
    );
  };

  const renderBiggestRow = (item, index, columnKeys = TIME_SALES_COLUMN_KEYS) => {
    if (item) {
      return renderItem(item, index, columnKeys, index);
    }

    const renderCell = (value, key, extra = "", isLast = false) => (
      <div
        key={key}
        className={`truncate px-3 py-2.5 text-center text-sm font-medium text-slate-500 ${extra} ${isLast ? "flex-1" : ""}`}
        style={{ width: `${columnWidths[key]}px`, minWidth: 60 }}
      >
        {value}
      </div>
    );

    return (
      <div
        key={`biggest-empty-${index}`}
        className={`flex min-w-full transition-colors ${
          theme === "dark" ? "text-slate-500" : "text-slate-400"
        } ${stripeRowClass(index)}`}
      >
        {columnKeys.map((key, idx) =>
          renderCell("—", key, "", idx === columnKeys.length - 1)
        )}
      </div>
    );
  };

  const renderBiggestSection = () => (
    <>
      {renderHeader(true)}
      {renderBiggestRow(biggestCall, 0)}
      {renderBiggestRow(biggestPut, 1)}
    </>
  );

  const renderTimeSaleRows = (items, startRowIndex = 0) =>
    items.map((item, idx) => renderItem(item, idx, TIME_SALES_COLUMN_KEYS, startRowIndex + idx));

  const renderTableShell = (children) => (
    <div className={tableFrameClass}>
      <div className="flex w-max min-w-full flex-col">{children}</div>
    </div>
  );

  const renderItem = (item, index, columnKeys = TIME_SALES_COLUMN_KEYS, rowIndex = index) => {
    const textColor = getColor(item?.type);
    const contractColor = getContractColor(item?.contract_type);

    const renderCell = (value, key, extra = "", isLast = false) => (
      <div
        key={key}
        className={`truncate px-3 py-2.5 text-center text-sm font-medium ${extra} ${isLast ? "flex-1" : ""} ${
          numericCellKeys.has(key) ? "tabular-nums" : ""
        }`}
        style={{ width: `${columnWidths[key]}px`, minWidth: 60 }}
      >
        {value}
      </div>
    );

    return (
      <div
        key={`${item.id}-${index}`}
        className={`flex min-w-full transition-colors ${
          theme === "dark" ? "text-slate-300" : "text-slate-700"
        } ${stripeRowClass(rowIndex)}`}
      >
        {columnKeys.map((key, idx) => {
          let extra = textColor;
          if (key === "option") extra = contractColor;
          else if (key === "market" || key === "delta" || key === "iv") {
            extra = `${textColor} font-mono text-xs opacity-90`;
          }

          const value = key === "time" ? formatLocalTradeDateTime(item?.time) : item?.[key];
          return renderCell(value, key, extra, idx === columnKeys.length - 1);
        })}
      </div>
    );
  };

  const tablesMainScrollClass = `min-h-0 flex-1 overflow-auto scrollbar-thin scrollbar-thumb-slate-700 scrollbar-track-transparent ${scrollSurfaceClass}`;

  const renderTimeSaleRowsBlock = (items, showLoader = false, lockTimeSale = false) => {
    if (showLoader) {
      return (
        <div
          className={`flex min-h-[10rem] flex-col items-center justify-center gap-3 border-t p-6 ${
            theme === "dark" ? "border-slate-700/70 bg-slate-900/40" : "border-slate-200 bg-white"
          }`}
        >
          <div className="h-8 w-8 animate-spin rounded-full border-2 border-blue-500/20 border-t-blue-500" />
          <span className="text-xs font-semibold uppercase tracking-widest text-slate-400">
            Loading time &amp; sales…
          </span>
        </div>
      );
    }

    return (
      <>
        <div className="flex w-max min-w-full flex-col">{renderTimeSaleRows(items)}</div>
        {subscribed && !lockTimeSale && isFetchingTimeSale && page > 1 && (
          <div
            className={`flex items-center justify-center gap-3 p-4 ${
              theme === "dark" ? "bg-slate-900/50" : "bg-slate-50"
            }`}
          >
            <div className="h-4 w-4 animate-spin rounded-full border-2 border-blue-500/20 border-t-blue-500" />
            <span className="text-xs font-bold uppercase tracking-widest text-slate-400">
              Loading more results...
            </span>
          </div>
        )}
      </>
    );
  };

  const renderScrollableTables = (items, lockTimeSale = false, showLoader = false) => {
    const onMainScroll =
      subscribed && !lockTimeSale ? handleScroll : undefined;

    const biggestBlock = showBiggestTable ? (
      <div className="mb-3 shrink-0">{renderTableShell(renderBiggestSection())}</div>
    ) : null;

    // When locked, the biggest table stays outside the blurred overlay so it remains visible.
    const body = (
      <>
        {lockTimeSale ? null : biggestBlock}
        {showLoader || items.length > 0 || lockTimeSale ? (
          <div className={tableFrameBorderClass}>
            <div className="flex w-max min-w-full flex-col">
              {renderHeader(false, TIME_SALES_COLUMN_KEYS, true)}
              {renderTimeSaleRowsBlock(items, showLoader && !lockTimeSale, lockTimeSale)}
            </div>
          </div>
        ) : null}
      </>
    );

    if (lockTimeSale) {
      return (
        <div className={`flex min-h-0 flex-1 flex-col overflow-y-auto overflow-x-hidden ${scrollSurfaceClass}`}>
          {/* Biggest table scrolls horizontally on its own; the locked area is clipped so the overlay stays centered */}
          {biggestBlock ? <div className="shrink-0 overflow-x-auto">{biggestBlock}</div> : null}
          <div className="relative min-h-[16rem] flex-1 overflow-hidden">
            <div className="pointer-events-none min-h-[16rem] select-none blur-[3px]">{body}</div>
            <SubscribeUnblurOverlay
              title="Unlock time & sales"
              description="Subscribe to view the complete options time and sales feed for this symbol."
            />
          </div>
        </div>
      );
    }

    return (
      <div onScroll={onMainScroll} className={tablesMainScrollClass}>
        {body}
      </div>
    );
  };

  const renderTablesLayout = (items, lockTimeSale = false) => {
    if (showOrderFlowEmpty) {
      return (
        <OrderFlowEmptyState
          theme={theme}
          ticker={selected}
          processing={false}
        />
      );
    }

    const showTablesScroll =
      showBiggestTable || items.length > 0 || showTimeSaleSectionLoader || lockTimeSale;

    return (
      <div className="flex h-full min-h-0 flex-1 flex-col overflow-hidden p-3">
        {showTablesScroll
          ? renderScrollableTables(items, lockTimeSale, showTimeSaleSectionLoader)
          : null}
      </div>
    );
  };

  return (
    <div className="flex h-full min-w-0 flex-1 flex-col overflow-hidden">
      {/* Header */}
      <div className={`relative z-[35] shrink-0 flex min-w-0 flex-col gap-3 overflow-visible border-b p-3 sm:flex-row sm:items-center sm:justify-between sm:p-4 ${
        theme === 'dark' ? 'border-slate-800 bg-slate-900/50' : 'border-slate-100 bg-slate-50/50'
      }`}>
        <div className="flex min-w-0 items-center gap-3">
          <div className={`shrink-0 rounded-lg p-2 bg-emerald-500/10 ${theme === 'dark' ? 'text-emerald-500' : 'text-emerald-600'}`}>
            <HiOutlineTable size={22} />
          </div>
          <div className="flex min-w-0 flex-1 flex-col gap-1 sm:flex-row sm:flex-wrap sm:items-center sm:gap-x-2 sm:gap-y-1">
            <h2 className={`text-base font-bold sm:text-lg ${theme === 'dark' ? 'text-slate-200' : 'text-slate-900'}`}>Options Time & Sales</h2>
            {subscribed ? (
              <RecencyBadge recency={recency} delayMinutes={delayMinutes} theme={theme} />
            ) : (
              <MarketDataDelaySubscribeInline theme={theme} delayMinutes={delayMinutes} />
            )}
          </div>
        </div>
        <div className="relative z-50 flex w-full min-w-0 shrink-0 items-center justify-end gap-2 overflow-visible sm:w-auto sm:gap-3">
           <SelectTicker
             selected={selected}
             setSelected={setSelected}
             clearResetsSelection={false}
           />
        </div>
      </div>

      {showIngestionProgressBar ? (
        <IngestionProgress ingestion={ingestion} theme={theme} />
      ) : null}

      {/* Table Container */}
      <div className={`relative min-w-0 flex-1 overflow-hidden ${scrollSurfaceClass}`}>
        {!hasTicker ? (
          <div className="flex h-full min-h-[16rem] flex-1 items-center justify-center p-6">
            <div
              className={`w-full max-w-md rounded-2xl border px-8 py-10 text-center shadow-xl ${
                theme === "dark"
                  ? "border-slate-700/70 bg-slate-900/70 shadow-black/30"
                  : "border-[#5244D3]/15 bg-white shadow-[#5244D3]/10"
              }`}
            >
              <div
                className={`mx-auto mb-5 flex h-16 w-16 items-center justify-center rounded-full border ${
                  theme === "dark"
                    ? "border-violet-400/25 bg-violet-500/15"
                    : "border-[#5244D3]/15 bg-[#5244D3]/10"
                }`}
              >
                <HiOutlineTable
                  size={30}
                  className={theme === "dark" ? "text-[#AAA2F5]" : "text-[#5244D3]"}
                />
              </div>
              <p
                className={`text-base font-semibold tracking-tight ${
                  theme === "dark" ? "text-slate-100" : "text-slate-900"
                }`}
              >
                Select a ticker to show data
              </p>
              <p className={`mt-2 text-sm leading-relaxed ${theme === "dark" ? "text-slate-400" : "text-slate-500"}`}>
                Search and select a symbol above to load options time &amp; sales and today&apos;s largest prints for that ticker.
              </p>
            </div>
          </div>
        ) : showFullPageLoader ? (
          <InitialDataLoader theme={theme} ingestionPercent={null} />
        ) : (
          <div className={`h-full min-h-0 min-w-0 ${scrollSurfaceClass}`}>
            {renderTablesLayout(data, !subscribed)}
          </div>
        )}
      </div>
    </div>
  );
};

export default OptionsTimeSales;
